import { useId } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  profileEditFormSchema,
  ProfileEditFormSchema,
  useProfile,
  useProfileEdit,
} from "@/api/profile";

// components
import { swalError, swalSuccess } from "@/utils/lib/swal";
import { FormContainer } from "@/components/form/form-container";
import { VerticalGaps } from "@/components/ui/container";
import { FormFooter } from "@/components/form/form-footer";
import { Anchor } from "@/components/ui/anchor";
import { SubmitButton } from "@/components/ui/button";
import { FieldInput } from "@/components/form/field-input";
import { Loading } from "@/components/ui/loading";
import { useITVDepartments } from "@/api/master-param";
import { FieldSelect } from "@/components/form/field-select";
import RoleAccordion from "./RoleAccordion";
import Icon from "@/components/ui/icon";
import { Building, Building2, Mail, MapPin, Phone, User } from "lucide-react";
import { H4 } from "@/components/ui/typography";

export default function EditProfileForm() {
  const formId = useId();
  const { data: user, isPending: isLoadingProfile } = useProfile();
  const { mutate, isPending } = useProfileEdit();

  const { data: departments } = useITVDepartments();
  const departmentOptions = departments?.content?.map((department) => ({
    value: department.value,
    label: department.value,
  }));

  const form = useForm<ProfileEditFormSchema>({
    resolver: zodResolver(profileEditFormSchema),
    defaultValues: {
      company: user?.company,
      department: user?.department ?? "",
      companyCode: user?.companyCode,
      email: user?.email,
      phone: user?.phone ?? "",
      address: user?.address ?? "",
    },
    values: user,
  });

  function handleSubmit(values: ProfileEditFormSchema) {
    mutate(
      { data: values },
      {
        onSuccess: () => {
          swalSuccess.fire({
            text: "Profile has been updated",
          });
        },
        onError: (error) => {
          swalError.fire({
            text: error.message,
          });
        },
      },
    );
  }

  if (isLoadingProfile) {
    return <Loading />;
  }

  return (
    <div className="flex w-full flex-col gap-4">
      <FormContainer id={formId} form={form} onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 gap-6 rounded-lg border p-4 shadow md:grid-cols-2">
          <div className="col-span-2 flex items-center gap-2">
            <User />
            <H4>User Information</H4>
          </div>
          <VerticalGaps>
            <div className="flex w-full gap-3">
              <Icon
                icon={<Building2 className="h-5 w-5 text-blue-600" />}
                backgroundClassName="bg-blue-50"
                size={42}
              />
              <FieldInput
                name="companyCode"
                label="Company Code"
                type="text"
                unstyle
                isDisabled
              />
            </div>
            <div className="flex w-full gap-3">
              <Icon
                icon={<Mail className="h-5 w-5 text-blue-600" />}
                backgroundClassName="bg-blue-50"
                size={42}
              />
              <FieldInput
                name="email"
                label="Email"
                type="text"
                unstyle
                isDisabled
              />
            </div>
            <div className="flex w-full gap-3">
              <Icon
                icon={<Building2 className="h-5 w-5 text-blue-600" />}
                backgroundClassName="bg-blue-50"
                size={42}
              />
              <FieldInput
                name="company"
                label="Company Name"
                type="text"
                unstyle
                isDisabled
              />
            </div>
          </VerticalGaps>
          <VerticalGaps>
            <div className="flex w-full gap-3">
              <Icon
                icon={<Building className="h-5 w-5 text-blue-600" />}
                backgroundClassName="bg-blue-50"
                size={42}
              />
              <FieldSelect
                name="department"
                label="Department"
                options={departmentOptions ?? []}
                unstyle
              />
            </div>
            <div className="flex w-full gap-3">
              <Icon
                icon={<Phone className="h-5 w-5 text-blue-600" />}
                backgroundClassName="bg-blue-50"
                size={42}
              />
              <FieldInput
                name="phone"
                label="No Telepon"
                type="text"
                transform={(e) => ({
                  ...e,
                  target: {
                    ...e.target,
                    value: (e.target.value ?? "").replaceAll(/\D/g, ""),
                  },
                })}
                unstyle
              />
            </div>
            <div className="flex w-full gap-3">
              <Icon
                icon={<MapPin className="h-5 w-5 text-blue-600" />}
                backgroundClassName="bg-blue-50"
                size={42}
              />
              <FieldInput name="address" label="Address" type="text" unstyle />
            </div>
          </VerticalGaps>
        </div>

        <div className="rounded-lg border px-4 shadow">
          <RoleAccordion />
        </div>
      </FormContainer>
      <FormFooter>
        <Anchor href="/" mood="axiata-blue-outline">
          Cancel
        </Anchor>
        <SubmitButton form={formId} loading={isPending} />
      </FormFooter>
    </div>
  );
}
